class MelProductPage {
  constructor(root) {
    this.root = root;
    this.header = root.querySelector('[data-mel-header]');
    this.megaTriggers = [...root.querySelectorAll('[data-mel-mega-trigger]')];
    this.megaMenus = [...root.querySelectorAll('[data-mel-mega-menu]')];
    this.mobileToggle = root.querySelector('[data-mel-menu-toggle]');
    this.mobileDrawer = root.querySelector('[data-mel-mobile-drawer]');
    this.mobileClose = root.querySelector('[data-mel-menu-close]');
    this.galleryTrack = root.querySelector('[data-mel-gallery-track]');
    this.slides = [...root.querySelectorAll('[data-mel-slide]')];
    this.thumbs = [...root.querySelectorAll('[data-mel-thumb]')];
    this.dots = [...root.querySelectorAll('[data-mel-dot]')];
    this.form = root.querySelector('.mel-product-form');
    this.variantInput = root.querySelector('[data-mel-variant-input]');
    this.variants = JSON.parse(root.querySelector('[data-mel-variants]')?.textContent || '[]');
    this.footerPanels = [...root.querySelectorAll('[data-mel-footer-panel]')];
    this.footerMedia = window.matchMedia('(max-width: 900px)');
    this.materialDialog = root.querySelector('[data-mel-material-dialog]');
    this.materialTrigger = root.querySelector('[data-mel-material-open]');
    this.materialClose = root.querySelector('[data-mel-material-close]');
    this.quantity = 1;
    this.currentSlide = 0;
    this.bindHeader();
    this.bindGallery();
    this.bindProduct();
    this.bindMaterialDialog();
    this.bindStickyCart();
    this.bindRecommendations();
    this.bindFooter();
  }

  bindHeader() {
    this.megaTriggers.forEach((trigger) => {
      const menu = this.megaMenus.find((item) => item.dataset.melMegaMenu === trigger.dataset.melMegaTrigger);
      if (menu && !menu.querySelector('a[href]')) {
        trigger.hidden = true;
      }
    });
    const announcementMessages = [...this.root.querySelectorAll('[data-mel-announcement-message]')];
    const announcementPrevious = this.root.querySelector('[data-mel-announcement-prev]');
    const announcementNext = this.root.querySelector('[data-mel-announcement-next]');
    let announcementIndex = 0;
    let announcementTimer;

    const showAnnouncement = (index) => {
      if (!announcementMessages.length) return;
      announcementIndex = (index + announcementMessages.length) % announcementMessages.length;
      announcementMessages.forEach((message, messageIndex) => {
        message.hidden = messageIndex !== announcementIndex;
      });
    };

    const restartAnnouncementTimer = () => {
      window.clearInterval(announcementTimer);
      if (announcementMessages.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      announcementTimer = window.setInterval(() => showAnnouncement(announcementIndex + 1), 5500);
    };

    announcementPrevious?.addEventListener('click', () => {
      showAnnouncement(announcementIndex - 1);
      restartAnnouncementTimer();
    });
    announcementNext?.addEventListener('click', () => {
      showAnnouncement(announcementIndex + 1);
      restartAnnouncementTimer();
    });
    showAnnouncement(0);
    restartAnnouncementTimer();

    const closeMegaMenus = (returnFocus = false) => {
      const activeTrigger = this.megaTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
      this.megaMenus.forEach((menu) => {
        menu.hidden = true;
      });
      this.megaTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (returnFocus) activeTrigger?.focus();
    };

    const openMegaMenu = (name, toggle = false) => {
      const trigger = this.megaTriggers.find((item) => item.dataset.melMegaTrigger === name);
      const menu = this.megaMenus.find((item) => item.dataset.melMegaMenu === name);
      if (!trigger || !menu) return;
      const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
      closeMegaMenus();
      if (!wasOpen || !toggle) {
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
      }
    };

    this.megaTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        // Hover already opened a desktop menu; the following click must not close it.
        const toggle = event.detail === 0 || !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        openMegaMenu(trigger.dataset.melMegaTrigger, toggle);
      });
      trigger.addEventListener('mouseenter', () => openMegaMenu(trigger.dataset.melMegaTrigger));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMegaMenu(trigger.dataset.melMegaTrigger);
          const menu = this.megaMenus.find((item) => item.dataset.melMegaMenu === trigger.dataset.melMegaTrigger);
          menu?.querySelector('a')?.focus();
        }
      });
    });

    this.header?.addEventListener('mouseleave', () => closeMegaMenus());
    this.header?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMegaMenus(true);
      }
    });

    const setMobileMenu = (open) => {
      if (!this.mobileDrawer || !this.mobileToggle) return;
      this.mobileDrawer.hidden = !open;
      this.mobileToggle.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('mel-menu-open', open);
      if (open) this.mobileClose?.focus();
      else {
        this.mobileDrawer.querySelectorAll('.mel-mobile-nav-group[open]').forEach((group) => {
          group.open = false;
        });
        this.mobileToggle.focus();
      }
    };

    const mobileGroups = [...(this.mobileDrawer?.querySelectorAll('.mel-mobile-nav-group') || [])];
    mobileGroups.forEach((group) => {
      group.addEventListener('toggle', () => {
        if (!group.open) return;
        mobileGroups.forEach((otherGroup) => {
          if (otherGroup !== group) otherGroup.open = false;
        });
      });
    });

    this.mobileToggle?.addEventListener('click', () => setMobileMenu(true));
    this.mobileClose?.addEventListener('click', () => setMobileMenu(false));
    this.mobileDrawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setMobileMenu(false);
    });

    document.addEventListener('click', (event) => {
      if (!this.header?.contains(event.target)) closeMegaMenus();
    });
  }

  bindGallery() {
    const showSlide = (index, behavior = 'smooth') => {
      if (!this.slides.length) return;
      const next = Math.max(0, Math.min(index, this.slides.length - 1));
      this.currentSlide = next;
      this.slides[next].scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
      [...this.thumbs, ...this.dots].forEach((control) => {
        const controlIndex = Number(control.dataset.melThumb ?? control.dataset.melDot);
        control.setAttribute('aria-current', String(controlIndex === next));
      });
    };

    this.root.querySelector('[data-mel-gallery-prev]')?.addEventListener('click', () => showSlide(this.currentSlide - 1));
    this.root.querySelector('[data-mel-gallery-next]')?.addEventListener('click', () => showSlide(this.currentSlide + 1));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => showSlide(Number(thumb.dataset.melThumb))));
    this.dots.forEach((dot) => dot.addEventListener('click', () => showSlide(Number(dot.dataset.melDot))));

    this.galleryTrack?.addEventListener('scroll', () => {
      const width = this.galleryTrack.clientWidth || 1;
      const index = Math.round(this.galleryTrack.scrollLeft / width);
      if (index !== this.currentSlide) {
        this.currentSlide = index;
        [...this.thumbs, ...this.dots].forEach((control) => {
          const controlIndex = Number(control.dataset.melThumb ?? control.dataset.melDot);
          control.setAttribute('aria-current', String(controlIndex === index));
        });
      }
    }, { passive: true });
  }

  bindProduct() {
    const optionGroups = [...this.root.querySelectorAll('[data-mel-option-group]')]
      .sort((a, b) => Number(a.dataset.melOptionPosition) - Number(b.dataset.melOptionPosition));
    const choices = [...this.root.querySelectorAll('[data-mel-option-choice]')];
    const price = this.root.querySelector('[data-mel-price]');
    const comparePrice = this.root.querySelector('[data-mel-compare-price]');
    const addButtons = [...this.root.querySelectorAll('[data-mel-add]')];
    const addLabels = [...this.root.querySelectorAll('[data-mel-add-label]')];
    const stickyLabels = [...this.root.querySelectorAll('[data-mel-sticky-label]')];
    const stickyOptions = [...this.root.querySelectorAll('[data-mel-sticky-option]')];
    const moneyFormat = window.Shopify?.currency?.active === 'USD' ? '${{amount}}' : '${{amount}}';
    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    };

    const setVariant = (variant) => {
      if (!variant || !this.variantInput) return;
      this.variantInput.value = variant.id;
      price.textContent = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.textContent = formatMoney(variant.compare_at_price);
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }
      addButtons.forEach((button) => {
        button.disabled = !variant.available;
      });
      addLabels.forEach((label) => {
        label.textContent = variant.available ? label.dataset.availableLabel || 'Add to cart' : label.dataset.soldLabel || 'Sold out';
      });
      stickyLabels.forEach((label) => {
        label.textContent = variant.available ? label.dataset.availableLabel || 'Add to bag' : label.dataset.soldLabel || 'Sold out';
      });
      stickyOptions.forEach((label) => {
        label.textContent = variant.title === 'Default Title' ? label.dataset.defaultLabel || 'One size' : variant.title;
      });
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', nextUrl);
    };

    const syncOptionGroups = () => {
      const selected = [];

      optionGroups.forEach((group, index) => {
        const inputs = [...group.querySelectorAll('[data-mel-option-choice]')];
        inputs.forEach((input) => {
          input.disabled = !this.variants.some((variant) => {
            if (!variant.available || variant.options[index] !== input.value) return false;
            return selected.every((value, previousIndex) => variant.options[previousIndex] === value);
          });
        });

        let checked = inputs.find((input) => input.checked && !input.disabled);
        if (!checked) {
          checked = inputs.find((input) => !input.disabled);
          if (checked) checked.checked = true;
        }

        if (checked) {
          selected.push(checked.value);
          const selectedLabel = group.querySelector(`[data-mel-selected-option="${index + 1}"]`);
          if (selectedLabel) selectedLabel.textContent = checked.value;
        }
      });

      return selected;
    };

    const updateFromOptions = () => {
      const selected = syncOptionGroups();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      setVariant(variant);
    };

    choices.forEach((choice) => choice.addEventListener('change', updateFromOptions));
    if (optionGroups.length) updateFromOptions();

    this.root.querySelector('[data-mel-scroll-options]')?.addEventListener('click', () => {
      optionGroups[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    const updateQuantity = (next) => {
      this.quantity = Math.max(1, Math.min(10, next));
      this.root.querySelectorAll('[data-mel-quantity]').forEach((output) => {
        output.textContent = String(this.quantity);
      });
      this.root.querySelectorAll('[data-mel-quantity-input]').forEach((input) => {
        input.value = String(this.quantity);
      });
    };

    this.root.querySelectorAll('[data-mel-quantity-minus]').forEach((button) => {
      button.addEventListener('click', () => updateQuantity(this.quantity - 1));
    });
    this.root.querySelectorAll('[data-mel-quantity-plus]').forEach((button) => {
      button.addEventListener('click', () => updateQuantity(this.quantity + 1));
    });
  }

  bindMaterialDialog() {
    if (!this.materialDialog || !this.materialTrigger) return;

    const openDialog = () => {
      if (typeof this.materialDialog.showModal === 'function') {
        this.materialDialog.showModal();
      } else {
        this.materialDialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('mel-material-open');
      this.materialClose?.focus();
    };

    const closeDialog = () => {
      if (typeof this.materialDialog.close === 'function') {
        this.materialDialog.close();
      } else {
        this.materialDialog.removeAttribute('open');
      }
    };

    this.materialTrigger.addEventListener('click', openDialog);
    this.materialClose?.addEventListener('click', closeDialog);
    this.materialDialog.addEventListener('click', (event) => {
      if (event.target === this.materialDialog) closeDialog();
    });
    this.materialDialog.addEventListener('close', () => {
      document.documentElement.classList.remove('mel-material-open');
      this.materialTrigger.focus();
    });
  }

  bindStickyCart() {
    const sticky = this.root.querySelector('[data-mel-sticky-cart]');
    const primary = this.root.querySelector('[data-mel-primary-add]');
    if (!sticky || !primary || !('IntersectionObserver' in window)) return;
    const footer = this.root.querySelector('.mel-footer');
    let primaryVisible = true;
    let footerVisible = false;
    const render = () => {
      sticky.classList.toggle('is-visible', !primaryVisible && !footerVisible && window.scrollY > primary.offsetTop);
    };
    const primaryObserver = new IntersectionObserver(([entry]) => {
      primaryVisible = entry.isIntersecting;
      render();
    }, { threshold: 0.2 });
    primaryObserver.observe(primary);
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        render();
      }, { threshold: 0 });
      footerObserver.observe(footer);
    }
  }

  bindRecommendations() {
    const track = this.root.querySelector('[data-mel-recommendations-track]');
    const previous = this.root.querySelector('[data-mel-recommendations-prev]');
    const next = this.root.querySelector('[data-mel-recommendations-next]');
    if (!track || !previous || !next) return;

    const getStep = () => {
      const card = track.querySelector('.mel-product-card');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * .8;
    };
    const updateControls = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    previous.addEventListener('click', () => track.scrollBy({ left: -getStep(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: getStep(), behavior: 'smooth' }));
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls, { passive: true });
    updateControls();
  }

  bindFooter() {
    if (!this.footerPanels.length) return;
    let wasMobile;
    const syncPanels = () => {
      const isMobile = this.footerMedia.matches;
      if (isMobile === wasMobile) return;
      this.footerPanels.forEach((panel) => {
        panel.open = !isMobile;
      });
      wasMobile = isMobile;
    };
    syncPanels();
    this.footerMedia.addEventListener?.('change', syncPanels);
  }
}

const initMelProductPages = (scope = document) => {
  scope.querySelectorAll('[data-mel-product-page]').forEach((root) => {
    if (!root.melProductPage) root.melProductPage = new MelProductPage(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMelProductPages());
} else {
  initMelProductPages();
}

document.addEventListener('shopify:section:load', (event) => initMelProductPages(event.target));
